import { Button, Dialog } from '@protoss/ui';
import type { StaffingCoverage } from '@protoss/shared';
import { useNavigate } from 'react-router-dom';

/** Where a workspace admin sets a product's role rates (Administration -> Workspace -> Resources). */
export function resourcesSettingsPath(productKey: string): string {
  return `/administration/workspace?tab=resources&product=${encodeURIComponent(productKey)}`;
}

/**
 * Blocking pop-up shown instead of generating or exporting priced output when the deal's
 * resource plan staffs roles that have no rate (Harman, 2026-10-20: "set up the real rates
 * before creating stuff, otherwise it should not even create that"). Without the rates the
 * estimate, SOW fee table and timeline come out as 0 hours / $0, so nothing is generated.
 *
 * `coverage` comes from GET /sales/deals/:dealUuid/staffing-coverage, or from the `details`
 * of a 409 STAFFING_RATES_MISSING - the same server check either way.
 */
export function StaffingRatesDialog({
  coverage,
  onClose,
}: {
  coverage: StaffingCoverage | null;
  onClose: () => void;
}) {
  const navigate = useNavigate();
  const product = coverage?.productName ?? 'this product';

  return (
    <Dialog.Root
      open={coverage !== null}
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      <Dialog.Portal>
        <Dialog.Overlay className="dialog-overlay" />
        <Dialog.Content className="dialog-content staffing-rates-dialog">
          <Dialog.Title className="dialog-title">Set staffing rates for {product} first</Dialog.Title>
          <Dialog.Description className="dialog-description">
            The {product} resource plan staffs these roles, but this workspace has no rate for
            them. Without rates the estimate, SOW fee table and timeline come out as 0 hours / $0,
            so nothing was generated.
          </Dialog.Description>
          <ul className="staffing-rates-dialog__roles">
            {(coverage?.missingRoles ?? []).map((role) => (
              <li key={role.roleKey} className="staffing-rates-dialog__role">
                <span className="staffing-rates-dialog__role-name">{role.roleName}</span>
                <span className="staffing-rates-dialog__role-key">{role.roleKey}</span>
                {role.status === 'zero_rate' ? (
                  <span className="staffing-rates-dialog__role-note">rate is 0</span>
                ) : null}
              </li>
            ))}
          </ul>
          {coverage?.canEdit ? null : (
            <p className="dialog-description">
              Ask a workspace admin to set them in Administration -&gt; Workspace -&gt; Resources
              -&gt; {product}.
            </p>
          )}
          <div className="staffing-rates-dialog__actions">
            <Button type="button" variant="secondary" size="sm" onClick={onClose}>
              Close
            </Button>
            {coverage?.canEdit ? (
              <Button
                type="button"
                variant="primary"
                size="sm"
                onClick={() => {
                  const productKey = coverage.productKey;
                  onClose();
                  navigate(resourcesSettingsPath(productKey));
                }}
              >
                Set rates
              </Button>
            ) : null}
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
